import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getBlogPost } from '../data/blogPosts';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd } from '../hooks/useJsonLd';
import { buildArticleSchema, buildBreadcrumbSchema } from '../utils/seoStructuredData';
import FooterSection from '../components/landing/FooterSection';
import SafeHtml from '../components/SafeHtml';
import BlogContentRenderer from '../components/BlogContentRenderer';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ApiPost {
  slug: string;
  title: string;
  description: string;
  html_content: string;
  cover_emoji: string;
  category: string;
  reading_time: string;
  author: string;
  tags: string[];
  published_at: string;
}

const _rawApiUrl = process.env.REACT_APP_API_URL || 'https://api.retrosive.com/api';
const API_BASE = _rawApiUrl.replace(/\/api\/?$/, '');

// ─── Page ────────────────────────────────────────────────────────────────────

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [apiPost, setApiPost] = useState<ApiPost | null | 'loading'>('loading');

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/api/blog/posts/${slug}/`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setApiPost(data))
      .catch(() => setApiPost(null));
  }, [slug]);

  // Fall back to static data while loading or if API returned null
  const staticPost = getBlogPost(slug ?? '');

  // Determine what to render
  const isApiPost = apiPost !== 'loading' && apiPost !== null;
  const post = isApiPost ? apiPost : staticPost;

  const title = isApiPost ? (apiPost as ApiPost).title : staticPost?.title ?? '';
  const description = isApiPost ? (apiPost as ApiPost).description : staticPost?.description ?? '';
  const publishedAt = isApiPost ? (apiPost as ApiPost).published_at : staticPost?.publishedAt ?? '';
  const author = isApiPost ? (apiPost as ApiPost).author : staticPost?.author ?? '';
  const tags = isApiPost ? (apiPost as ApiPost).tags : staticPost?.tags ?? [];
  const category = isApiPost ? (apiPost as ApiPost).category : staticPost?.category ?? '';
  const readingTime = isApiPost ? (apiPost as ApiPost).reading_time : String(staticPost?.readingTime ?? '');

  useSEO(
    post
      ? {
          title,
          description,
          canonical: `/blog/${slug}`,
          keywords: tags,
          ogType: 'article',
          article: {
            publishedTime: publishedAt,
            author,
            tags,
          },
        }
      : {
          title: 'Post Not Found',
          description: 'This blog post does not exist.',
          robots: 'noindex, nofollow',
        }
  );

  const jsonLd = useMemo(() => {
    if (!post || !slug) return null;
    return [
      buildArticleSchema({
        path: `/blog/${slug}`,
        headline: title,
        description,
        datePublished: publishedAt,
        keywords: tags,
      }),
      buildBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: title, path: `/blog/${slug}` },
      ]),
    ];
  }, [post, slug, title, description, publishedAt, tags]);

  useJsonLd(jsonLd);

  // Still loading from API and no static fallback either
  if (apiPost === 'loading' && !staticPost) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!post && apiPost !== 'loading') {
    return <Navigate to="/blog" replace />;
  }

  if (!post) return null;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 text-white pt-16 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-orange-300 hover:text-white text-sm mb-8 transition-colors"
          >
            ← All posts
          </Link>

          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-semibold text-orange-300 bg-orange-500/20 px-2 py-0.5 rounded-full">
              {category}
            </span>
            <span className="text-slate-400 text-xs">{readingTime} min read</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">{title}</h1>
          <p className="text-slate-300 text-lg mb-6">{description}</p>

          <div className="flex items-center gap-3 text-sm text-slate-400">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">
              R
            </div>
            <span>{author}</span>
            <span>·</span>
            <span>{formatDate(publishedAt)}</span>
          </div>
        </div>
      </section>

      {/* Article body */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {isApiPost ? (
          <SafeHtml
            html={(apiPost as ApiPost).html_content}
            className="prose prose-slate prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:text-gray-700 prose-ol:text-gray-700 prose-a:text-orange-500 prose-a:no-underline hover:prose-a:underline max-w-none"
          />
        ) : (
          staticPost && <BlogContentRenderer sections={staticPost.content} />
        )}
      </article>

      {/* Tags */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <div className="flex flex-wrap gap-2 pt-8 border-t border-gray-200">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-full"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* More posts */}
      <div className="bg-slate-50 border-t border-gray-200 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">More from the blog</h2>
          <div className="flex gap-2">
            <Link
              to="/blog"
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium transition-colors"
            >
              ← All posts
            </Link>
            <Link
              to="/register"
              className="px-5 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-semibold transition-colors"
            >
              Try Retrosive Free
            </Link>
          </div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
};

export default BlogPostPage;
